import { Card, CardBody, CardFooter, CardHeader, CardHeading, Loading, SubmitButton, TextField } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { tenantService } from "../shared/api/tenantService";
import { TenantMultiSelect } from "../shared/select/TenantSelect";
import { UserCreateFormValues } from "../shared/types/user";
import { isValidEmail } from "../shared/utils/email";
import { useUserForm } from "./useUserForm";

export const UserCreate: FC = () => {
	const { t } = useTranslation();
	const { createUser } = useUserForm();

	const { data: defaultTenant, isLoading } = useQuery({
		queryKey: tenantService.getDefaultKey,
		queryFn: () => tenantService.getDefault().then((r) => r.data.data),
	});

	const initialValues: UserCreateFormValues = useMemo(
		() => ({
			firstName: null,
			lastName: null,
			email: null,
			position: null,
			company: null,
			tenants: defaultTenant ? [{ ...defaultTenant }] : [],
		}),
		[defaultTenant],
	);

	const validate = (values: UserCreateFormValues) => {
		const errors: { [key in keyof UserCreateFormValues]?: string } = {};
		if (!values.firstName) {
			errors.firstName = t("Required field");
		}
		if (!values.lastName) {
			errors.lastName = t("Required field");
		}
		if (!values.email) {
			errors.email = t("Required field");
		}
		if (values.email && !isValidEmail(values.email)) {
			errors.email = t("Invalid email address");
		}
		if (!values.tenants || values.tenants.length === 0) {
			errors.tenants = t("Required field");
		}
		return errors;
	};

	return (
		<Card>
			<CardHeader>
				<CardHeading>{t("Create user")}</CardHeading>
			</CardHeader>
			{isLoading ? (
				<Loading />
			) : (
				<Formik initialValues={initialValues} validate={validate} onSubmit={createUser}>
					<Form>
						<CardBody>
							<TextField label={t("Firstname")} name="firstName" required />
							<TextField label={t("Lastname")} name="lastName" required />
							<TextField label={t("Email")} name="email" required />
							<TextField label={t("Company")} name="company" />
							<TextField label={t("Position")} name="position" />
							<TenantMultiSelect name="tenants" label={t("Tenants")} required />
						</CardBody>
						<CardFooter>
							<SubmitButton>{t("Save")}</SubmitButton>
						</CardFooter>
					</Form>
				</Formik>
			)}
		</Card>
	);
};
